import * as React from "react";
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { createStore, type StoreApi, type StateCreator } from "zustand/vanilla";
import { useStore } from "zustand";
import { User } from "./models/users";
import { api } from "./api";

type CurrentSong = { id: number; playing: boolean };

export interface QueuedSong {
  name: string;
  uploaded_url: string;
  entry_url: string;
  entry_id: number;
  artists?: string[];
}

export type UserState =
  | { type: "logged-in"; user: User }
  | { type: "loading" }
  | { type: "logged-out" };

type Store = {
  // State
  user: UserState;
  currentSong: CurrentSong | undefined;
  queue: QueuedSong[];
  currentQueueIndex: number;

  // Actions
  setUser: (user: UserState) => void;
  fetchUser: (force?: boolean) => Promise<void>;
  setSong: (song: CurrentSong) => void;
  queueSong: (song: QueuedSong) => void;
  playSongNow: (song: QueuedSong) => void;
  removeSong: (index: number) => void;
  clearQueue: () => void;
  setCurrentQueueIndex: (index: number) => void;
};

// Track if a fetch is in progress to avoid duplicate requests
let fetchInProgress = false;

const store: StoreApi<Store> = createStore<Store>(((set, get) => ({
  user: { type: "loading" },
  currentSong: undefined,
  queue: [],
  currentQueueIndex: 0,

  setUser: (user: UserState) => set({ user }),

  fetchUser: async (force = false) => {
    const currentUser = get().user;
    // Don't refetch if we already have a result (unless forced)
    if (!force && currentUser.type !== "loading") {
      return;
    }
    // Avoid duplicate concurrent fetches
    if (fetchInProgress) {
      return;
    }

    fetchInProgress = true;
    set({ user: { type: "loading" } });
    try {
      const userData = await api.get("/api/user");
      set({
        user: userData
          ? { type: "logged-in", user: new User(userData) }
          : { type: "logged-out" },
      });
    } catch {
      set({ user: { type: "logged-out" } });
    } finally {
      fetchInProgress = false;
    }
  },

  setSong: (song: CurrentSong) => set({ currentSong: song }),

  queueSong: (song: QueuedSong) =>
    set((state) => ({ queue: [...state.queue, song] })),

  playSongNow: (song: QueuedSong) =>
    set((state) => ({
      queue: [...state.queue, song],
      currentQueueIndex: state.queue.length,
    })),

  removeSong: (index: number) =>
    set((state) => {
      const queue = state.queue.filter((_, i) => i !== index);
      let newIndex = state.currentQueueIndex;

      if (index === state.currentQueueIndex) {
        newIndex = Math.min(index, queue.length - 1);
      } else if (index < state.currentQueueIndex) {
        newIndex = state.currentQueueIndex - 1;
      }

      return {
        queue,
        currentQueueIndex: Math.max(0, newIndex),
      };
    }),

  clearQueue: () => set({ queue: [], currentQueueIndex: 0 }),

  setCurrentQueueIndex: (index: number) => set({ currentQueueIndex: index }),
})) as StateCreator<Store>);

export const userStore = store;

export const useUserStore = <T,>(selector: (state: Store) => T): T =>
  useStore(store, selector);

export const setUser = (value: UserState): void => {
  store.getState().setUser(value);
};

export const getUser = (): UserState => store.getState().user;

export const setSong = (song: CurrentSong): void => {
  store.getState().setSong(song);
};

export const useSong = (): CurrentSong | undefined =>
  useStore(store, (state) => state.currentSong);

// Queue exports
export const useQueue = () =>
  useStore(store, (state) => ({
    songs: state.queue,
    currentIndex: state.currentQueueIndex,
  }));

export const queueSong = (song: QueuedSong): void => {
  store.getState().queueSong(song);
};

export const playSongNow = (song: QueuedSong): void => {
  store.getState().playSongNow(song);
};

export const removeSongFromQueue = (index: number): void => {
  store.getState().removeSong(index);
};

export const clearQueue = (): void => {
  store.getState().clearQueue();
};

export const setCurrentQueueIndex = (index: number): void => {
  store.getState().setCurrentQueueIndex(index);
};

export const getQueue = () => ({
  songs: store.getState().queue,
  currentIndex: store.getState().currentQueueIndex,
});

type UserContextValue = {
  user: UserState;
  refreshUser: () => Promise<void>;
};

export const UserContext = createContext<UserContextValue>({
  user: { type: "loading" },
  refreshUser: async () => {},
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const user = useStore(store, (state) => state.user);
  const fetchUser = useStore(store, (state) => state.fetchUser);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value = useMemo(
    () => ({
      user,
      refreshUser: () => fetchUser(true),
    }),
    [user, fetchUser]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext).user;
export const useUserStatus = () => useContext(UserContext);

// Audio element reference - set by the AudioPlayer component
let audioElementRef: HTMLAudioElement | null = null;

export const registerAudioElement = (el: HTMLAudioElement | null): void => {
  audioElementRef = el;
};

export const getAudioElement = (): HTMLAudioElement | null => audioElementRef;

export const playAudio = (): void => {
  audioElementRef?.play();
};

export const pauseAudio = (): void => {
  audioElementRef?.pause();
};
